import React, { useState } from 'react';
import { api, openReceipt } from '../lib/api';
import { fmtDate, money, specialEffect, today } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Task, TaskEvent } from '../lib/types';
import { Confirm, Drawer, Loading, type Ctx } from '../main';

const STATUS_LABEL: Record<string, string> = {
  Pending: 'Pending', InProgress: 'In progress', Done: 'Done', Closed: 'Closed',
};
const METHOD_LABEL: Record<string, string> = {
  BankTransfer: 'bank transfer', Cash: 'cash', CreditCard: 'card', Cheque: 'cheque',
};

/**
 * One task: what it is, how it got here, and what can still change.
 *
 * Workflow, type and name are shown and never editable — they decide what the
 * engine does when the task closes. Status moves between Pending and In
 * progress here; Done is reached only by completing it, which runs the engine.
 */
export function TaskDrawer({ ctx, task, onClose, onChanged }: {
  ctx: Ctx; task: Task; onClose: () => void; onChanged: (message?: string) => Promise<void>;
}): React.JSX.Element {
  const detail = useResource(() => api.task(task.id), [task.id]);
  const rooms = useResource(() => api.rooms(task.propertyId).catch(() => ({ rooms: [] })), [task.propertyId]);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isMoney = task.type !== 'Action';
  const specialist = !ctx.canManage;
  const [form, setForm] = useState({
    status: task.status, assigneeId: task.assigneeId ?? '', dueDate: task.dueDate,
    roomId: task.roomId ?? '', amount: String(task.amount || ''), currency: task.currency,
    description: task.description,
  });

  const t = detail.data?.task ?? task;
  // the catalogue's copy: one shape, already loaded
  const rule = ctx.ruleFor(t.name);
  const events = detail.data?.events ?? [];
  const notice = events.find((e) => e.event === 'tenant_payment_reported');
  const room = rooms.data?.rooms.find((r) => r.id === t.roomId);
  const late = t.dueDate < today();

  const save = async (): Promise<void> => {
    const patch: Record<string, unknown> = {};
    if (form.status !== task.status) patch.status = form.status;
    if (form.description !== task.description) patch.description = form.description;
    if (!specialist) {
      if (form.assigneeId !== (task.assigneeId ?? '')) patch.assigneeId = form.assigneeId || null;
      if (form.dueDate !== task.dueDate) patch.dueDate = form.dueDate;
      if (form.roomId !== (task.roomId ?? '')) patch.roomId = form.roomId || null;
      if (isMoney && Number(form.amount) !== task.amount) patch.amount = Number(form.amount);
      if (isMoney && form.currency !== task.currency) patch.currency = form.currency;
    }
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    setBusy(true); setError(null);
    try {
      await api.updateTask(task.id, patch);
      await onChanged('Task saved');
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const view = async (id: string): Promise<void> => {
    try { await openReceipt(id); } catch (e) { setError((e as Error).message); }
  };

  if (deleting) {
    return (
      <Confirm title="Delete this task?" danger action="Delete task"
        detail="Only for a task raised by mistake. A task the engine created from another one cannot be deleted while its successors exist."
        onClose={() => setDeleting(false)}
        onConfirm={async () => { await api.deleteTask(task.id); await onChanged('Task deleted'); }} />
    );
  }

  return (
    <Drawer title={t.name}
      subtitle={`${t.workflow} · ${t.propertyName ?? ctx.propertyName(t.propertyId)}${room ? ` · Room ${room.number}` : ''}`}
      onClose={onClose}
      footer={editing ? <>
        <button className="btn" onClick={() => setEditing(false)}>Cancel</button>
        <button className="btn accent" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>
      </> : <>
        {ctx.canManage && (
          <button className="btn quiet w" style={{ marginRight: 'auto', color: 'var(--coral)' }}
            onClick={() => setDeleting(true)}>Delete</button>
        )}
        <button className="btn w" onClick={() => setEditing(true)}>Edit</button>
        <button className="btn accent w" onClick={() => ctx.complete(t)}>Complete…</button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}

      {notice && (
        <div className="note warn" style={{ marginBottom: 14 }}>
          <b>The tenant says they have paid</b>
          {' '}by {METHOD_LABEL[String(notice.payload?.method)] ?? String(notice.payload?.method)}
          {notice.payload?.reference ? <> — reference <span className="mono">{String(notice.payload.reference)}</span></> : null}
          , on {fmtDate(notice.created_at)}. Check it against the bank, then complete this task with the receipt.
        </div>
      )}

      {editing ? (
        <>
          <div className="frow">
            <div className="field">
              <label>Workflow</label>
              <input value={t.workflow} disabled />
            </div>
            <div className="field">
              <label>Type</label>
              <input value={t.type} disabled />
            </div>
          </div>
          <div className="hint" style={{ marginTop: -6, marginBottom: 12 }}>
            Locked — they define what happens when the task closes.
          </div>
          <div className="field">
            <label htmlFor="e-status">Status</label>
            <select id="e-status" value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as Task['status'] })}>
              <option value="Pending">Pending</option>
              <option value="InProgress">In progress</option>
            </select>
            <div className="hint">Done is reached by completing the task — that is what runs the engine.</div>
          </div>
          {specialist && (
            <div className="note" style={{ marginBottom: 12 }}>
              You can start this task and add notes. Reassigning, re-dating or re-pricing is a manager’s call.
            </div>
          )}
          <div className="frow">
            <div className="field">
              <label htmlFor="e-who">Assigned to</label>
              <select id="e-who" value={form.assigneeId} disabled={specialist}
                onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}>
                <option value="">— unassigned —</option>
                {ctx.employees.filter((e) => e.is_active || e.id === task.assigneeId).map((e) => (
                  <option key={e.id} value={e.id}>{e.name} — {e.role}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="e-due">Due date</label>
              <input id="e-due" type="date" value={form.dueDate} disabled={specialist}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="e-room">Room (optional)</label>
            <select id="e-room" value={form.roomId} disabled={specialist}
              onChange={(e) => setForm({ ...form, roomId: e.target.value })}>
              <option value="">— none —</option>
              {(rooms.data?.rooms ?? []).map((r) => <option key={r.id} value={r.id}>Room {r.number}</option>)}
            </select>
          </div>
          {isMoney && (
            <div className="frow">
              <div className="field">
                <label htmlFor="e-amt">Amount</label>
                <input id="e-amt" type="number" min={0} value={form.amount} disabled={specialist}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="e-cur">Currency</label>
                <select id="e-cur" value={form.currency} disabled={specialist}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  {['AED', 'USD', 'EUR'].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="field">
            <label htmlFor="e-desc">Notes</label>
            <textarea id="e-desc" rows={4} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </>
      ) : (
        <>
          <dl className="kv">
            <dt>Type</dt><dd><span className={`tag t-${t.type}`}>{t.type}</span></dd>
            <dt>Status</dt><dd><span className={`tag t-${t.status}`}>{STATUS_LABEL[t.status]}</span></dd>
            <dt>Assigned to</dt><dd>{ctx.employeeName(t.assigneeId) ?? <span className="sub">nobody yet</span>}</dd>
            <dt>Due</dt><dd className={`mono ${late ? 'late' : ''}`}>{fmtDate(t.dueDate)}{late ? ' · overdue' : ''}</dd>
            {isMoney && <><dt>Amount</dt><dd className="mono">{money(t.amount, t.currency)}</dd></>}
            {t.severity && <><dt>Severity</dt><dd><span className={`tag ${t.severity === 'Critical' ? 't-sos' : t.severity === 'High' ? 't-bad' : 't-mute'}`}>{t.severity}</span></dd></>}
            {(t.source || t.category) && <><dt>Reported</dt><dd>{[t.category, t.source].filter(Boolean).join(' · ')}</dd></>}
            {t.description && <><dt>Notes</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{t.description}</dd></>}
            {t.receiptId && (
              <><dt>Attached</dt><dd>
                <button className="btn sm" onClick={() => void view(t.receiptId!)}>
                  {t.source === 'TenantApp' ? 'Open the tenant’s photo' : 'Open the attachment'}
                </button>
              </dd></>
            )}
          </dl>

          <h3 className="sec" style={{ fontSize: 13 }}>How this task got here</h3>
          {detail.loading && <Loading />}
          <div className="chain">
            {events.map((e) => <EventNode key={e.id} event={e} />)}
            {(detail.data?.children ?? []).map((c) => (
              <div key={c.id} className={`node ${c.status === 'Done' ? 'done' : ''}`}>
                <b style={{ color: 'var(--muted)' }}>{c.name}</b>
                <span>Created by this task · {STATUS_LABEL[c.status] ?? c.status}</span>
              </div>
            ))}
          </div>
          {rule && (
            <div className="note" style={{ marginTop: 12 }}>
              {rule.question
                ? <>Completing this asks: <b>{rule.question.prompt}</b></>
                : rule.special
                  ? <>Completing this {specialEffect(rule.special)}.</>
                  : rule.next.length
                    ? <>Completing this creates: {rule.next.join(', ')}.</>
                    : <>Completing this ends the chain.</>}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}

function EventNode({ event }: { event: TaskEvent }): React.JSX.Element {
  const when = new Date(event.created_at).toLocaleString('en-GB',
    { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  const fields = Array.isArray(event.payload?.fields) ? (event.payload!.fields as string[]).join(', ') : '';
  const said: Record<string, string> = {
    created: event.payload?.manual ? 'Created by hand' : 'Created by the engine',
    updated: `Edited${fields ? `: ${fields}` : ''}`,
    completed: `Completed${event.resolution ? ` — ${event.resolution}` : ''}`,
    tenant_payment_reported: 'The tenant reported a payment',
  };
  const by = event.actor_type === 'system' ? 'the engine' : event.actor_type;
  return (
    <div className={`node ${event.event === 'completed' ? 'done' : ''}`}>
      <b>{said[event.event] ?? event.event}</b>
      <span>{when} · {by} · {event.surface}</span>
    </div>
  );
}
